import { h } from '@/lib/dom';

export interface Column<Row> {
  key: string;
  header: string;
  render?: (row: Row) => Node | string;
  align?: 'left' | 'right' | 'center';
  width?: string;
}

export interface TableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  onRowClick?: (row: Row) => void;
  ariaLabel?: string;
  empty?: string;
}

export function Table<Row extends Record<string, unknown>>(props: TableProps<Row>): HTMLElement {
  if (!props.rows.length && props.empty) {
    return h('div', { class: 'empty-state', role: 'status' }, props.empty);
  }
  return h('table', {
    class: 'data-table',
    role: 'table',
    aria: props.ariaLabel ? { label: props.ariaLabel } : undefined,
  }, [
    h('thead', null,
      h('tr', null, props.columns.map((col) =>
        h('th', {
          scope: 'col',
          style: col.width ? `width:${col.width}; text-align:${col.align ?? 'left'};` : `text-align:${col.align ?? 'left'};`,
        }, col.header),
      ))),
    h('tbody', null,
      props.rows.map((row) => {
        const tr = h('tr', {
          class: props.onRowClick ? 'clickable' : '',
          tabindex: props.onRowClick ? 0 : undefined,
          role: props.onRowClick ? 'button' : undefined,
          on: props.onRowClick
            ? {
                click: () => props.onRowClick!(row),
                keydown: (ev: Event) => {
                  const ke = ev as KeyboardEvent;
                  if (ke.key === 'Enter' || ke.key === ' ') {
                    ev.preventDefault();
                    props.onRowClick!(row);
                  }
                },
              }
            : undefined,
        }, props.columns.map((col) => {
          const content = col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '');
          return h('td', { style: `text-align:${col.align ?? 'left'};` },
            typeof content === 'string' || typeof content === 'number' ? String(content) : content);
        }));
        return tr;
      })),
  ]);
}
